import { db } from "../../core/data/local/db";
import { uuidv7 } from "../../core/platform/id";
import type { TicketDocument } from "../../core/platform/receipt/compose-ticket";
import { DrawerTransportError, type DrawerTransport, type FailureCode } from "./transport";

/**
 * The print queue (T005–T007, specs/002-barista-order-tickets/research.md R4).
 *
 * PERSISTED THE SAME WAY A DRAWER ATTEMPT IS, because it's the same kind of
 * fact: a hardware attempt that must survive a reload. One `db.moduleRecords`
 * row per job (`kind: "ticket.printJob"`), keyed by `opId`, mirroring
 * `recorder.ts` exactly.
 *
 * LOCAL ONLY — NOT `db.outbox`. A print job never needs a server; FR-213
 * forbids any network dependency on this path, and giving a print job a
 * `syncedAt`/server-ack lifecycle it doesn't need would just be dead fields.
 *
 * NEVER MORE THAN ONE WRITE IN FLIGHT. The worker drains strictly in
 * `createdAt` order, and job *n+1* never starts before job *n* has reached
 * `printed` or `failed` (FR-218) — the same discipline
 * `webbluetooth-transport.ts` already applies one level down, to a single
 * ticket's byte chunks.
 */

const MODULE_ID = "peripherals";
const RECORD_KIND = "ticket.printJob";

/** How long a queued job may sit before a reload stops trusting it (FR-220). */
export const STALENESS_THRESHOLD_MS = 10 * 60 * 1000;

export type TicketPrintJobState = "waiting" | "printing" | "printed" | "failed" | "stale";

export interface TicketPrintJob {
  opId: string;
  saleId: string;
  document: TicketDocument;
  state: TicketPrintJobState;
  attempts: number;
  lastError: FailureCode | null;
  createdAt: string;
  printedAt: string | null;
}

type Listener = (jobs: readonly TicketPrintJob[]) => void;

export interface TicketQueueDeps {
  transport: Pick<DrawerTransport, "write">;
  /** Renders a composed ticket to the bytes the transport should send. */
  render: (document: TicketDocument) => Uint8Array;
  shiftId: () => string | null;
  now?: () => number;
}

/**
 * A job never regresses out of `printed`. There is no `printed → anything`
 * edge — a printed ticket is a fact about the physical world (paper already
 * came out) that no later software state can undo.
 */
export class TicketQueue {
  #jobs = new Map<string, TicketPrintJob>();
  #listeners = new Set<Listener>();
  #draining = false;
  readonly #now: () => number;

  constructor(private readonly deps: TicketQueueDeps) {
    this.#now = deps.now ?? Date.now;
  }

  get jobs(): readonly TicketPrintJob[] {
    return [...this.#jobs.values()].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  subscribe(listener: Listener): () => void {
    this.#listeners.add(listener);
    listener(this.jobs);
    return () => this.#listeners.delete(listener);
  }

  #emit(): void {
    const snapshot = this.jobs;
    for (const listener of this.#listeners) listener(snapshot);
  }

  /**
   * Enqueue a composed ticket. Returns once the job is durably recorded as
   * `waiting` — NOT once it prints (FR-211, R7). The worker drains in the
   * background; the caller (the `sale.finalized` subscriber) never awaits it.
   */
  async enqueue(saleId: string, document: TicketDocument): Promise<TicketPrintJob> {
    const job: TicketPrintJob = {
      opId: uuidv7(),
      saleId,
      document,
      state: "waiting",
      attempts: 0,
      lastError: null,
      createdAt: new Date(this.#now()).toISOString(),
      printedAt: null,
    };

    await this.#persist(job);
    this.#jobs.set(job.opId, job);
    this.#emit();

    void this.#drain();
    return job;
  }

  /**
   * Load jobs written before this session — a reload, a device restart. Past
   * the staleness threshold, a job is surfaced rather than resumed (FR-220):
   * a queued ticket is the same kind of fact `capability.ts` already refuses
   * to trust across a session boundary. A `printing` job found here was
   * interrupted mid-write with no way to know how much reached the printer,
   * so it restarts from `waiting` rather than resuming — reprinting a ticket
   * is a barista's mild annoyance; silently dropping the rest of one is not.
   */
  async loadPending(): Promise<void> {
    const rows = await db.moduleRecords.where("moduleId").equals(MODULE_ID).toArray();
    const threshold = this.#now() - STALENESS_THRESHOLD_MS;

    for (const row of rows) {
      if (row.kind !== RECORD_KIND) continue;
      const job = row.record as TicketPrintJob;
      if (job.state !== "waiting" && job.state !== "printing") continue;

      const isStale = new Date(job.createdAt).getTime() < threshold;
      const next: TicketPrintJob = isStale
        ? { ...job, state: "stale" }
        : { ...job, state: "waiting" };

      await this.#persist(next);
      this.#jobs.set(next.opId, next);
    }

    this.#emit();
    void this.#drain();
  }

  /** Re-queue a `failed` or `stale` job. A no-op on any other state. */
  async retry(opId: string): Promise<void> {
    const job = this.#jobs.get(opId);
    if (!job || (job.state !== "failed" && job.state !== "stale")) return;

    const next: TicketPrintJob = { ...job, state: "waiting", lastError: null };
    await this.#persist(next);
    this.#jobs.set(opId, next);
    this.#emit();

    void this.#drain();
  }

  async #drain(): Promise<void> {
    if (this.#draining) return;
    this.#draining = true;

    try {
      // Re-read `this.jobs` each iteration — enqueue() during a drain must be
      // picked up rather than requiring a second #drain() call.
      let next = this.jobs.find((job) => job.state === "waiting");
      while (next) {
        await this.#printOne(next);
        next = this.jobs.find((job) => job.state === "waiting");
      }
    } finally {
      this.#draining = false;
    }
  }

  async #printOne(job: TicketPrintJob): Promise<void> {
    const printing: TicketPrintJob = { ...job, state: "printing", attempts: job.attempts + 1 };
    await this.#persist(printing);
    this.#jobs.set(job.opId, printing);
    this.#emit();

    try {
      const bytes = this.deps.render(job.document);
      await this.deps.transport.write(bytes);

      const printed: TicketPrintJob = {
        ...printing,
        state: "printed",
        printedAt: new Date(this.#now()).toISOString(),
      };
      await this.#persist(printed);
      this.#jobs.set(job.opId, printed);
    } catch (cause) {
      const code: FailureCode = cause instanceof DrawerTransportError ? cause.code : "kick_timeout";
      const failed: TicketPrintJob = { ...printing, state: "failed", lastError: code };
      await this.#persist(failed);
      this.#jobs.set(job.opId, failed);
    }

    this.#emit();
  }

  async #persist(job: TicketPrintJob): Promise<void> {
    await db.moduleRecords.put({
      opId: job.opId,
      moduleId: MODULE_ID,
      kind: RECORD_KIND,
      shiftId: this.deps.shiftId(),
      record: job,
      createdAt: job.createdAt,
    });
  }
}

/** Unretried failures/staleness for a shift — what FR-217 puts in close-out. */
export async function unresolvedTicketJobs(shiftId: string): Promise<TicketPrintJob[]> {
  const rows = await db.moduleRecords.where("shiftId").equals(shiftId).toArray();
  return rows
    .filter((row) => row.kind === RECORD_KIND)
    .map((row) => row.record as TicketPrintJob)
    .filter((job) => job.state === "failed" || job.state === "stale");
}
